import dayjs from 'dayjs';

import { getErrorMessage } from 'shared/helpers/getErrorMessage';
import { reformatNumber } from 'shared/helpers/formatNumber';

import { addNotify } from 'features/notify';

import { DAY_MILLISECONDS } from '../data';

const actionTypes = {
  ACTION_PROCESSING: 'reports/ACTION_PROCESSING',
  GET_STATISTIC_PROVIDER: 'reports/GET_STATISTIC_PROVIDER',
  ACTIVE_USERS_PROCESSING: 'reports/ACTIVE_USERS_PROCESSING',
  GET_ACTIVE_USERS_STATISTIC: 'reports/GET_ACTIVE_USERS_STATISTIC',
  GET_ACTIVE_USERS_TIMESERIES: 'reports/GET_ACTIVE_USERS_TIMESERIES',
  GET_REPORT_TIMESERIES: 'reports/GET_REPORT_TIMESERIES',
  GET_PROVIDER_TIMESERIES: 'reports/GET_PROVIDER_TIMESERIES',
  GET_PROVIDER_RANKS: 'reports/GET_PROVIDER_RANKS',
  CLEAR_PROVIDER_DETAILS: 'reports/CLEAR_PROVIDER_DETAILS',
  GET_SAVED_FILTERS: 'reports/GET_SAVED_FILTERS',
  ADD_SAVED_FILTER: 'reports/ADD_SAVED_FILTER',
  DELETE_SAVED_FILTER: 'reports/DELETE_SAVED_FILTER',
  GET_PAYMENT_HISTORY: 'reports/GET_PAYMENT_HISTORY',
  GET_PAYMENT_HISTORY_TOTAL: 'reports/GET_PAYMENT_HISTORY_TOTAL',
  GET_USER_STATISTICS: 'reports/GET_USER_STATISTICS',
  GET_SLOTS_HISTORY: 'reports/GET_SLOTS_HISTORY',
  GET_BETS_HISTORY: 'reports/GET_BETS_HISTORY',
  GET_BET_INFO: 'reports/GET_BET_INFO',
  GET_PLAYER_DEPOSITS: 'reports/GET_PLAYER_DEPOSITS',
  GET_CBU: 'reports/GET_CBU',
  GET_REQUEST_HISTORY: 'reports/GET_REQUEST_HISTORY',
  GET_CASHBACK_HISTORY: 'reports/GET_CASHBACK_HISTORY',
  ACTIVATE_CASHBACK_FOR_ONE_USER: 'reports/ACTIVATE_CASHBACK_FOR_ONE_USER',
  ACTIVATE_CASHBACK_FOR_STRUCTURE: 'reports/ACTIVATE_CASHBACK_FOR_STRUCTURE',

  // SET_POLLING_FILTERS: 'reports/SET_POLLING_FILTERS',
};

const getDiffByPercent = (current, prev) => {
  const currentValue = reformatNumber(String(current ?? 0));
  const previousValue = reformatNumber(String(prev ?? 0));
  const result = (currentValue / previousValue - 1) * 100;

  if (Number(previousValue) === 0) return 0;

  return Math.round(result * 100) / 100 || 0;
};

const roundPercent = value => Math.round(value * 100) / 100 || 0;

const getIncomeAmountDiffByPercent = (current, prev) => {
  const currentValue = Number(reformatNumber(String(current ?? 0)));
  const prevValue = Number(reformatNumber(String(prev ?? 0)));

  if (!prevValue) return 0;

  const result =
    prevValue < 0 && currentValue > 0
      ? ((currentValue - prevValue) / Math.abs(prevValue)) * 100
      : (currentValue / prevValue - 1) * 100;

  return roundPercent(result);
};

const getStatisticDiff = (current, previous) => ({
  betCount: previous.betCount,
  betCountPercent: getDiffByPercent(current.betCount, previous.betCount),
  betAmount: previous.betAmount,
  betAmountPercent: getDiffByPercent(current.betAmount, previous.betAmount),
  winAmount: previous.winAmount,
  winAmountPercent: getDiffByPercent(current.winAmount, previous.winAmount),
  incomeAmount: previous.incomeAmount,
  incomeAmountPercent: getIncomeAmountDiffByPercent(current.incomeAmount, previous.incomeAmount),
  commissionBalance: previous.commissionBalance,
  commissionBalancePercent: getDiffByPercent(current.commissionBalance, previous.commissionBalance),
  margin: previous.margin,
  marginPercent: getDiffByPercent(current.margin, previous.margin),
  activeUsers: previous.activeUsers,
  activeUsersPercent: getDiffByPercent(current.activeUsers, previous.activeUsers),
  rtp: previous.rtp,
  rtpPercent: getDiffByPercent(current.rtp, previous.rtp),
});

function getStatisticProvider(form) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { selectedAgentId } = getState().tree;
    const { isFullStatistic = false, requestKey } = form;
    if (isFullStatistic && getState().reports.activeUsersStatistic.isLoading) return;
    const processingType = isFullStatistic ? actionTypes.ACTIVE_USERS_PROCESSING : actionTypes.ACTION_PROCESSING;
    const resultType = isFullStatistic ? actionTypes.GET_ACTIVE_USERS_STATISTIC : actionTypes.GET_STATISTIC_PROVIDER;

    const requestedBody = {
      date_from: dayjs(form.date.beginDate).format('YYYY-MM-DD'),
      date_to: dayjs(form.date.endDate).format('YYYY-MM-DD'),
      is_direct_only: form.structure.name === 'directOnly' ? true : false,
      is_full_statistic: isFullStatistic,
    };

    if (selectedAgentId) {
      dispatch({ type: processingType, payload: true, requestKey });

      try {
        const response = await api.reports.getStatisticProvider({ id: selectedAgentId, requestedBody });
        /* const isFromOutdated = dayjs(requestedBody.date_from).isBefore('2024-03-05')
          && dayjs(requestedBody.date_from).isAfter('2024-02-29') ;

        const isToOutdated = dayjs(requestedBody.date_to).isBefore('2024-03-05')
          && dayjs(requestedBody.date_to).isAfter('2024-02-29') ;

        const response = await api.reports.getStatisticProvider({ id: selectedAgentId, requestedBody, isOutdated: isFromOutdated || isToOutdated }); */

        if (form.withDiff && response.success) {
          const requestedBodyDiff = {
            date_from: dayjs(form.diffDate.compareFrom).format('YYYY-MM-DD'),
            date_to: dayjs(form.diffDate.compareTo).format('YYYY-MM-DD'),
            is_direct_only: form.structure.name === 'directOnly' ? true : false,
            is_full_statistic: isFullStatistic,
          };

          const diffResponse = await api.reports.getStatisticProvider({
            id: selectedAgentId,
            requestedBody: requestedBodyDiff,
          });

          if (diffResponse.success && diffResponse.data?.detail && response.data?.detail) {
            const diffDetail = diffResponse.data.detail;
            const currentDetail = response.data.detail;
            const diff = getStatisticDiff(currentDetail, diffDetail);
            const providersDiff = {};
            response.data.providers.forEach(currentProvider => {
              const diffProvider = diffResponse.data.providers.find(
                provider => provider.providerKey === currentProvider.providerKey,
              );

              if (currentProvider && diffProvider) {
                providersDiff[currentProvider.providerKey] = {
                  ...getStatisticDiff(currentProvider, diffProvider),
                  provider: diffProvider.provider,
                  providerKey: diffProvider.providerKey,
                };
              }
            });
            dispatch({
              type: resultType,
              payload: {
                ...response.data,
                diff,
                providersDiff,
              },
            });
          } else {
            const { locale } = getState().locale;
            const errorMessage = getErrorMessage(locale, diffResponse);
            dispatch(addNotify(errorMessage, 'error'));
          }
        } else if (response.success) {
          dispatch({
            type: resultType,
            payload: response.data,
          });
        } else {
          const { locale } = getState().locale;
          const errorMessage = getErrorMessage(locale, response);
          dispatch(addNotify(errorMessage, 'error'));
        }
      } catch (error) {
        dispatch(addNotify(getErrorMessage(getState().locale.locale, error), 'error'));
      } finally {
        dispatch({ type: processingType, payload: false });
      }
    } else return;
  };
}

const ARGENTINA_TIMEZONE_OFFSET = -3;

const getTimeseriesRequest = form => ({
  date_from: dayjs(form.date.beginDate).format('YYYY-MM-DD'),
  date_to: dayjs(form.date.endDate).format('YYYY-MM-DD'),
  previous_date_from: dayjs(form.diffDate.compareFrom).format('YYYY-MM-DD'),
  previous_date_to: dayjs(form.diffDate.compareTo).format('YYYY-MM-DD'),
  tz: ARGENTINA_TIMEZONE_OFFSET,
});

function getReportTimeseries(form) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { selectedAgentId } = getState().tree;
    if (!selectedAgentId) return;
    const isActiveUsers = form.metric === 'active_users';
    if (isActiveUsers && getState().reports.activeUsersStatistic.isLoading) return;
    if (isActiveUsers) {
      dispatch({ type: actionTypes.ACTIVE_USERS_PROCESSING, payload: true, timeseriesKey: form.requestKey });
    }

    try {
      const response = await api.reports.getStatisticProviderTimeseries({
        id: selectedAgentId,
        requestedBody: {
          ...getTimeseriesRequest(form),
          metric: form.metric,
          ...(isActiveUsers ? { is_direct_only: form.structure.name === 'directOnly' } : {}),
        },
      });

      if (response.success) {
        dispatch({
          type: isActiveUsers ? actionTypes.GET_ACTIVE_USERS_TIMESERIES : actionTypes.GET_REPORT_TIMESERIES,
          payload: response.data,
        });
      } else {
        const { locale } = getState().locale;
        dispatch(addNotify(getErrorMessage(locale, response), 'error'));
      }
    } catch (error) {
      dispatch(addNotify(getErrorMessage(getState().locale.locale, error), 'error'));
    } finally {
      if (isActiveUsers) dispatch({ type: actionTypes.ACTIVE_USERS_PROCESSING, payload: false });
    }
  };
}

function getProviderRanks({ provider, date }) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { selectedAgentId } = getState().tree;
    if (!selectedAgentId || !provider) return;

    const response = await api.reports.getProviderRanks({
      id: selectedAgentId,
      provider,
      requestedBody: {
        date_from: dayjs(date.beginDate).format('YYYY-MM-DD'),
        date_to: dayjs(date.endDate).format('YYYY-MM-DD'),
      },
    });

    if (response.success) {
      dispatch({ type: actionTypes.GET_PROVIDER_RANKS, payload: response.data });
    } else {
      const { locale } = getState().locale;
      dispatch(addNotify(getErrorMessage(locale, response), 'error'));
    }
  };
}

const clearProviderDetails = () => ({ type: actionTypes.CLEAR_PROVIDER_DETAILS });

function getProviderTimeseries(form) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { selectedAgentId } = getState().tree;
    if (!selectedAgentId || !form.provider || !form.metrics.length) return;

    const responses = await Promise.all(
      form.metrics.map(metric =>
        api.reports.getProviderTimeseries({
          id: selectedAgentId,
          provider: form.provider,
          requestedBody: {
            ...getTimeseriesRequest(form),
            metric,
          },
        }),
      ),
    );
    const failedResponse = responses.find(response => !response.success);

    if (!failedResponse) {
      const data = form.metrics.reduce((result, metric, index) => {
        result[metric] = responses[index].data;
        return result;
      }, {});
      dispatch({ type: actionTypes.GET_PROVIDER_TIMESERIES, payload: data });
    } else {
      const { locale } = getState().locale;
      dispatch(addNotify(getErrorMessage(locale, failedResponse), 'error'));
    }
  };
}

function getSavedFilters() {
  return async (dispatch, getState, extra) => {
    const response = await extra.api.reports.getStatisticFilters();
    if (response.success) {
      dispatch({ type: actionTypes.GET_SAVED_FILTERS, payload: response.data });
    } else {
      const { locale } = getState().locale;
      dispatch(addNotify(getErrorMessage(locale, response), 'error'));
    }
  };
}

function createSavedFilter(form, callback) {
  return async (dispatch, getState, extra) => {
    const response = await extra.api.reports.createStatisticFilter(form);
    if (response.success) {
      dispatch({ type: actionTypes.ADD_SAVED_FILTER, payload: response.data });
      if (callback) callback(response.data);
    } else {
      const { locale } = getState().locale;
      dispatch(addNotify(getErrorMessage(locale, response), 'error'));
    }
  };
}

function deleteSavedFilter(id) {
  return async (dispatch, getState, extra) => {
    const response = await extra.api.reports.deleteStatisticFilter(id);
    if (response.success) {
      dispatch({ type: actionTypes.DELETE_SAVED_FILTER, payload: id });
    } else {
      const { locale } = getState().locale;
      dispatch(addNotify(getErrorMessage(locale, response), 'error'));
    }
  };
}

export default function validatePaymentHistoryForm(form) {
  const dateFrom = dayjs(form.beginDate);
  const dateTo = dayjs(form.endDate);

  const duration = dateTo.diff(dateFrom);

  return duration <= DAY_MILLISECONDS;
}

function getPaymentHistory(form) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const {
      locale: { locale },
      tree: { selectedAgentId },
      auth: { id: authId },
    } = getState();

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

    const validationResult = validatePaymentHistoryForm(form);

    if (!validationResult) {
      dispatch(addNotify(locale.reports.paymentHistoryError, 'error'));
      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
      return null;
    }

    const requestedBody = {
      date_from: dayjs(form.beginDate).format('YYYY-MM-DDTHH:mm:ss'),
      date_to: dayjs(form.endDate).format('YYYY-MM-DDTHH:mm:ss'),
      username: form.username,
      role: form.role,
      is_direct_structure: form.isDirectOnly,
      is_higher_transaction_only: form.isHigher,
      is_withdrawal_transfers: form.isWithdrawal,
      is_deposit_transfers: form.isDeposit,
      page: form.page,
      count: form.count,
      is_bonus_deposits: form.isBonus,
      transfers_only: true,
    };

    const requestId = form.username ? authId : selectedAgentId;

    if (requestId) {
      const response = await api.reports.getPaymentHistory({ id: requestId, requestedBody });

      if (response.success) {
        dispatch({
          type: actionTypes.GET_PAYMENT_HISTORY,
          payload: response.data,
        });
      } else {
        const errorMessage = getErrorMessage(locale, response);
        dispatch(addNotify(errorMessage, 'error'));
      }

      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
    } else {
      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
      return null;
    }
  };
}

function getPaymentHistoryTotals(form) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const {
      locale: { locale },
      tree: { selectedAgentId },
      auth: { id: authId },
    } = getState();

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

    const requestedBody = {
      date_from: dayjs(form.beginDate).format('YYYY-MM-DDTHH:mm:ss'),
      date_to: dayjs(form.endDate).format('YYYY-MM-DDTHH:mm:ss'),
      username: form.username,
      role: form.role,
      is_direct_structure: form.isDirectOnly,
      is_higher_transaction_only: form.isHigher,
      is_withdrawal_transfers: form.isWithdrawal,
      is_deposit_transfers: form.isDeposit,
      page: form.page,
      count: form.count,
      is_bonus_deposits: form.isBonus,
      transfers_only: false,
    };

    const requestId = form.username ? authId : selectedAgentId;

    if (requestId) {
      const response = await api.reports.getPaymentHistoryTotals({ id: requestId, requestedBody });

      if (response.success) {
        dispatch({
          type: actionTypes.GET_PAYMENT_HISTORY_TOTAL,
          payload: response.data,
        });
      } else {
        const errorMessage = getErrorMessage(locale, response);
        dispatch(addNotify(errorMessage, 'error'));
      }

      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
    } else {
      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
      return;
    }
  };
}

function getStatisticPlayer(form) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { selectedAgentId } = getState().tree;

    const requestedBody = {
      date_from: dayjs(form.beginDate).format('YYYY-MM-DD'),
      date_to: dayjs(form.endDate).format('YYYY-MM-DD'),
      username: form.username,
      is_direct_structure: form.isDirectOnly,
      page: form.page,
      count: form.count,
    };

    if (selectedAgentId) {
      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

      const response = await api.reports.getStatisticPlayer({ id: selectedAgentId, requestedBody });

      if (response.success) {
        dispatch({
          type: actionTypes.GET_USER_STATISTICS,
          payload: response.data,
        });
      } else {
        const { locale } = getState().locale;
        const errorMessage = getErrorMessage(locale, response);
        dispatch(addNotify(errorMessage, 'error'));
      }

      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
    } else {
      return;
    }
  };
}

function getSlotsHistory(form) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { selectedAgentId } = getState().tree;

    const requestedBody = {
      date_from: dayjs(form.beginDate).format('YYYY-MM-DDTHH:mm:ss'),
      date_to: dayjs(form.endDate).format('YYYY-MM-DDTHH:mm:ss'),
      username: form.username,
      page: form.page,
      count: form.count,
      game_type: form.gameType,
      provider: form.provider,
      status: form.status,
    };
    if (selectedAgentId) {
      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

      const response = await api.reports.getSlotsHistory({ id: selectedAgentId, requestedBody });

      if (response.success) {
        dispatch({
          type: actionTypes.GET_SLOTS_HISTORY,
          payload: response.data,
        });
      } else {
        const { locale } = getState().locale;
        const errorMessage = getErrorMessage(locale, response);
        dispatch(addNotify(errorMessage, 'error'));
      }

      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
    } else {
      return;
    }
  };
}

function getBetsHistory(requestedBody) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { selectedAgentId } = getState().tree;

    if (selectedAgentId) {
      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

      const response = await api.reports.getBetsHistory({ id: selectedAgentId, requestedBody });

      if (response.success) {
        dispatch({
          type: actionTypes.GET_BETS_HISTORY,
          payload: response.data,
        });
      } else {
        const { locale } = getState().locale;
        const errorMessage = getErrorMessage(locale, response);
        dispatch(addNotify(errorMessage, 'error'));
      }

      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
    } else return;
  };
}

function getBetInfo({ betId, userId }) {
  return async (dispatch, getState, extra) => {
    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });
    const { api } = extra;

    const response = await api.reports.getBetInfo({ betId, userId });
    if (response.success) {
      dispatch({
        type: actionTypes.GET_BET_INFO,
        payload: response.data,
      });
    } else {
      const { locale } = getState().locale;
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
    }
  };
}

function getPlayerDeposits(requestedBody) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });
    const response = await api.cbuPayment.getPlayerDeposits(requestedBody);

    if (response.success) {
      dispatch({
        type: actionTypes.GET_PLAYER_DEPOSITS,
        payload: response.data,
      });
      // dispatch({
      //   type: actionTypes.SET_POLLING_FILTERS,
      //   payload: requestedBody,
      // });
    } else {
      const { locale } = getState().locale;
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function changePlayerDeposit(requestedBody, callback) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });
    const response = await api.cbuPayment.changePlayerDeposit(requestedBody);

    if (response.success) {
      if (callback) callback();
    } else {
      const { locale } = getState().locale;
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function setDepositBonusPercent(requestedBody, callback) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;

    /* dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true }); */
    const response = await api.cbuPayment.setDepositBonusPercent(requestedBody);

    if (response.success) {
      if (callback) callback();
    } else {
      const { locale } = getState().locale;
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    /* dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false }); */
  };
}

function createBank(requestedBody, callback) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });
    const response = await api.cbuPayment.createBank(requestedBody);

    if (response.success) {
      if (callback) callback();
    } else {
      const { locale } = getState().locale;
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function updateBank(requestedBody, callback) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });
    const response = await api.cbuPayment.updateBank(requestedBody);

    if (response.success) {
      if (callback) callback();
    } else {
      const { locale } = getState().locale;
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function deleteBank(requestedBody, callback) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });
    const response = await api.cbuPayment.deleteBank(requestedBody);

    if (response.success) {
      if (callback) callback();
    } else {
      const { locale } = getState().locale;
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function getRequestHistory(requestedBody) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { selectedAgentId } = getState().tree;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });
    const response = await api.reports.getRequestHistory({
      requestedBody: { ...requestedBody, role: 0, status: 1 },
      id: selectedAgentId,
    });

    if (response.success) {
      dispatch({ type: actionTypes.GET_REQUEST_HISTORY, payload: response.data });
    } else {
      const { locale } = getState().locale;
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function getCashbackHistory(form) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const {
      locale: { locale },
    } = getState();

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

    const requestedBody = {
      date_from: dayjs(form.beginDate).format('YYYY-MM-DD'),
      date_to: dayjs(form.endDate).format('YYYY-MM-DD'),
      username: form.username || null,
      user_id: form.id || null,
      count: form.count,
      page: form.page,
    };

    const response = await api.reports.getCashbackHistory(requestedBody);

    if (response.success) {
      dispatch({
        type: actionTypes.GET_CASHBACK_HISTORY,
        payload: response.data,
      });
    } else {
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function activateCashbackForOneUser(form, callback) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const {
      locale: { locale },
    } = getState();

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

    const requestedBody = {
      activate: form.activate,
      user_id: form.id || null,
    };

    const response = await api.reports.activateCashbackForOneUser(requestedBody);

    if (response.success) {
      dispatch({
        type: actionTypes.ACTIVATE_CASHBACK_FOR_ONE_USER,
        payload: response.data,
      });
      dispatch(addNotify(locale.defaultSuccess, 'success'));
      if (callback && typeof callback === 'function') callback();
    } else {
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function activateCashbackForStructure(form) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const {
      locale: { locale },
    } = getState();

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

    const requestedBody = {
      activate: form.activate,
      is_direct_structure: form.isDirectStructureOnly || undefined,
      is_users_only: form.isPlayersOnly || undefined,
      is_agents_only: form.isAgentsOnly || undefined,
    };

    const response = await api.reports.activateCashbackForStructure(requestedBody);

    if (response.success) {
      dispatch({
        type: actionTypes.ACTIVATE_CASHBACK_FOR_STRUCTURE,
        payload: response.data,
      });
      dispatch(addNotify(locale.defaultSuccess, 'success'));
    } else {
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

export {
  actionTypes,
  getStatisticProvider,
  getReportTimeseries,
  getProviderRanks,
  getProviderTimeseries,
  clearProviderDetails,
  getSavedFilters,
  createSavedFilter,
  deleteSavedFilter,
  getPaymentHistory,
  getStatisticPlayer,
  getSlotsHistory,
  getBetsHistory,
  getPaymentHistoryTotals,
  getBetInfo,
  getPlayerDeposits,
  changePlayerDeposit,
  setDepositBonusPercent,
  createBank,
  updateBank,
  deleteBank,
  getRequestHistory,
  getCashbackHistory,
  activateCashbackForOneUser,
  activateCashbackForStructure,
};
